import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import BookCard from "../components/BookCard";
import KineticLines from "../components/KineticLines";
import Seo from "../components/Seo";

const FILTERS = [
  { key: "all", label: "Tous les romans" },
  { key: "historique", label: "Roman historique" },
  { key: "aventure", label: "Aventure" },
  { key: "anticipation", label: "Anticipation" },
  { key: "civilisations", label: "Civilisations" },
  { key: "contemporain", label: "Roman contemporain" },
  { key: "publie", label: "Publié", status: "publie" },
  { key: "a_paraitre", label: "À paraître", status: "a_paraitre" },
  { key: "en_preparation", label: "En préparation", status: "en_preparation" },
];

export default function NovelsList() {
  const [novels, setNovels] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => { api.get("/novels").then((r) => setNovels(r.data)); }, []);

  const filtered = useMemo(() => {
    if (active === "all") return novels;
    const f = FILTERS.find((x) => x.key === active);
    if (f?.status) return novels.filter((n) => n.status === f.status);
    return novels.filter((n) =>
      (n.tags || []).map((t) => t.toLowerCase()).includes(active) ||
      (n.genre || "").toLowerCase().includes(active)
    );
  }, [novels, active]);

  return (
    <div>
      <Seo
        path="/romans"
        title="Romans"
        description="Catalogue complet des romans de Bruno Courtin : trilogie Malo (Les routes secrètes de l'océan Indien), Terra Australis, Les Chaînes du Vivant, Mauritia, L'Arbre qui cachait la forêt, Le Feu intérieur, Les Héritages du vivant, Elle."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Romans de Bruno Courtin",
          url: "https://brunocourtin.fr/romans",
          inLanguage: "fr-FR",
          hasPart: novels.map((n) => ({
            "@type": "Book",
            name: n.title + (n.subtitle ? ` — ${n.subtitle}` : ""),
            author: { "@type": "Person", name: "Bruno Courtin" },
            url: `https://brunocourtin.fr/romans/${n.slug}`,
            genre: n.genre,
          })),
        }}
      />
      <section className="theme-ivory pt-40 pb-16 px-6 lg:px-12" data-testid="novels-hero">
        <div className="max-w-[1440px] mx-auto">
          <p className="eyebrow" style={{ color: "var(--copper)" }}>Catalogue</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] mt-4 text-[var(--ink)]">
            <KineticLines lines={["Romans"]} delay={0.1} />
          </h1>
          <p className="mt-8 max-w-2xl text-[16px] leading-relaxed text-[var(--brown)]/85">
            Sagas maritimes, anticipations, récits contemporains — l'ensemble des œuvres en cours,
            à paraître et en préparation.
          </p>
        </div>
      </section>

      <section className="theme-ivory px-6 lg:px-12 pb-8" data-testid="novels-filters">
        <div className="max-w-[1440px] mx-auto flex flex-wrap gap-2 border-y border-[var(--line)] py-6">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActive(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-4 py-2 text-[11px] font-mono uppercase tracking-widest transition-all ${
                active === f.key ? "bg-[var(--ink)] text-[var(--ivory)]" : "text-[var(--brown)] hover:text-[var(--ink)]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <section className="theme-ivory px-6 lg:px-12 pb-32">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mt-12">
          {filtered.map((n, i) => <BookCard key={n.id} novel={n} index={i} />)}
        </div>
        {filtered.length === 0 && (
          <p className="max-w-[1440px] mx-auto mt-16 text-center text-[var(--brown)]/70 font-mono text-sm">
            Aucun roman ne correspond à ce filtre.
          </p>
        )}
      </section>
    </div>
  );
}
